import React, { useEffect } from 'react';
import ReactDOM from 'react-dom';
import { Check, Stethoscope, X } from 'lucide-react';
import { IconTile } from './primitives';

/**
 * Bottom sheet listing the rotations that live behind the picker segment.
 * Each row shows the rotation's icon and tagline (from its data file) and
 * a check on the one that is open.
 */
const RotationPicker = ({ title, rotations, activeId, accent, onSelect, onClose }) => {
  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    const { overflow } = document.body.style;
    document.body.style.overflow = 'hidden';
    return () => { window.removeEventListener('keydown', onKey); document.body.style.overflow = overflow; };
  }, [onClose]);

  return ReactDOM.createPortal(
    <div className="fixed inset-0 z-50 flex items-end justify-center bg-black/40 animate-fadeIn" onClick={onClose}>
      <div
        role="dialog"
        aria-label={title}
        onClick={(e) => e.stopPropagation()}
        className="w-full max-w-md bg-white dark:bg-gray-800 rounded-t-3xl shadow-2xl pb-[max(1rem,env(safe-area-inset-bottom))] animate-slide-up"
      >
        <div className="flex items-center justify-between px-5 pt-4 pb-2">
          <h2 className="font-display text-lg font-semibold text-gray-900 dark:text-white">{title}</h2>
          <button onClick={onClose} aria-label="Close" className="p-2 -m-2 rounded-full text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700">
            <X size={20} />
          </button>
        </div>
        <div className="px-3 max-h-[70vh] overflow-y-auto">
          {rotations.map((r) => {
            const active = r.id === activeId;
            return (
              <button
                key={r.id}
                onClick={() => { onSelect(r.id); }}
                aria-current={active ? 'true' : undefined}
                className={`w-full flex items-center gap-3 px-3 py-3 rounded-2xl text-left ${active ? 'bg-gray-50 dark:bg-gray-700/60' : 'hover:bg-gray-50 dark:hover:bg-gray-700/40'}`}
              >
                <IconTile icon={r.data?.icon || Stethoscope} accent={r.data?.accent || accent} />
                <div className="flex-1 min-w-0">
                  <div className="text-[15px] font-semibold text-gray-900 dark:text-white leading-tight">{r.label}</div>
                  {r.data?.tagline && <div className="text-xs text-gray-500 dark:text-gray-400 mt-0.5 leading-snug">{r.data.tagline}</div>}
                </div>
                {active && <Check size={18} style={{ color: accent }} className="flex-shrink-0" />}
              </button>
            );
          })}
        </div>
      </div>
    </div>,
    document.body
  );
};

export default RotationPicker;
